import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
export const wpcApi = createApi({
  reducerPath: "wpcApi",
  tagTypes: [
    "getData",
    "clerkUsers",
    "getSingleData",
    "getCompanies",
    "jobs",
    "partners",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BASE_URL}`,
  }),
  endpoints: (builder) => ({
    getAll: builder.query({
      query: (clerkId) => ({
        url: `dashboard/`,
        method: "GET",
        params: { clerkId },
      }),
      providesTags: ["getData"],
    }),
    getUser: builder.query({
      query: ({ clerkId, route }) => ({
        url: route || `dashboard/user`,
        method: "GET",
        params: { clerkId },
      }),
      providesTags: ["getSingleData"],
    }),
    getSingle: builder.query({
      query: (id) => `dashboard/${id}`,
    }),
    checkStatus: builder.mutation({
      query: (clerkId) => ({
        url: "/dashboard",
        method: "POST",
        params: { clerkId },
      }),
      invalidatesTags: ["getData"],
    }),
    assignToken: builder.mutation({
      query: ({ clerkId, id }) => ({
        url: `dashboard/${clerkId}/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["getData"],
    }),
    tokenEmail: builder.mutation({
      query: ({ clerkId, id }) => ({
        url: `dashboard/token-email/${clerkId}/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["getData"],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `dashboard/${id}`,
        method: "DELETE",
        params: { id },
      }),
      invalidatesTags: ["getData", "getSingleData"],
    }),
    updateUser: builder.mutation({
      query: ({ id, fileStatus, body }) => ({
        url: `dashboard/${id}/${fileStatus}`,
        method: "PUT",
        body,
      }),
    }),
    basic: builder.mutation({
      query: ({ id, body }) => ({
        url: `/step-two`,
        method: "POST",
        body,
      }),
    }),
    verification: builder.mutation({
      query: ({ id, body }) => ({
        url: `/step-three`,
        method: "POST",
        body,
      }),
    }),
    additional: builder.mutation({
      query: ({ id, body }) => ({
        url: `/step-four/${id}`,
        method: "POST",
        body,
      }),
    }),
    getBasic: builder.query({
      query: ({ resumeId, applicationType }) => ({
        url: "/step-two",
        method: "GET",
        params: { resumeId, applicationType },
      }),
    }),
    getVerification: builder.query({
      query: ({ clerkId, _id }) => ({
        url: "/step-three",
        method: "GET",
        params: { clerkId, _id },
      }),
    }),
    getAdditional: builder.query({
      query: ({ clerkId, _id }) => ({
        url: "/step-four",
        method: "GET",
        params: { clerkId, _id },
      }),
    }),
    updateUserPartial: builder.mutation({
      query: ({ clerkId, id, body }) => ({
        url: `dashboard/${clerkId}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getData"],
    }),
    getClerkUsers: builder.query({
      query: (clerkId) => ({
        url: `clerk-users/${clerkId}`,
        method: "GET",
      }),
      providesTags: ["clerkUsers", "getData"],
    }),
    updateClerkUserRole: builder.mutation({
      query: ({ clerkId, userId, role }) => ({
        url: `clerk-users/${clerkId}/${userId}/${role}`,
        method: "PATCH",
      }),
      invalidatesTags: ["clerkUsers"],
    }),
    assignApplication: builder.mutation({
      query: ({ adminId, applicationId, status, clerkId }) => ({
        url: `/assignments/${clerkId}/${adminId}/${applicationId}/${status}`,
        method: "PATCH",
      }),
      invalidatesTags: ["getData"], // Invalidate relevant tags to refresh any cached data
    }),
    getClerkAdminData: builder.query({
      query: (clerkId) => ({
        url: `clerk-users/assignments/${clerkId}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),

    createWorkOrder: builder.mutation({
      query: ({ userId, body }) => ({
        url: `/create-workorder/${userId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["getData"],
    }),
    createCompany: builder.mutation({
      query: (formData) => ({
        url: "/create-company",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["getCompanies"],
    }),

    fetchAllCompanies: builder.query({
      query: () => ({
        url: `/fetch-companies`,
        method: "GET",
      }),
      providesTags: ["getCompanies"],
    }),
    deleteCompany: builder.mutation({
      query: (id) => ({
        url: `/companies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getCompanies"],
    }),
    uploadInvoice: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/invoices/${id}`,
        method: "POST",
        body: formData, // Send FormData as body
      }),
      invalidatesTags: ["getData"],
    }),
    deleteInvoice: builder.mutation({
      query: ({ id, invoiceId }) => ({
        url: `/invoices/${id}/${invoiceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getData"],
    }),
    updateInvoiceStatus: builder.mutation({
      query: ({ id, invoiceId, status }) => ({
        url: `/invoices/${id}/${invoiceId}/${status}`,
        method: "PATCH",
      }),
      invalidatesTags: ["getData", "getSingleData"],
    }),
    deleteUserFile: builder.mutation({
      query: ({ userId, fieldName, filePath }) => ({
        url: "/delete-file",
        method: "POST",
        body: { userId, fieldName, filePath },
      }),
    }),
    //Jobs
    createJob: builder.mutation({
      query: (body) => ({
        url: "/jobs",
        method: "POST",
        body,
      }),
      invalidatesTags: ["jobs"], // Invalidate job-related data if needed
    }),
    getJobs: builder.query({
      query: ({ page = 1, limit = 10, jobCategory, clerkId }) => ({
        url: "/jobs",
        method: "GET",
        params: { page, limit, jobCategory, clerkId },
      }),
      providesTags: ["jobs"],
    }),
    updateJobStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/jobs/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["jobs"],
    }),
    searchJobs: builder.query({
      query: (params) => ({
        url: "/jobs/search",
        method: "GET",
        params, // already includes page, limit, keyword, etc.
      }),
      providesTags: ["jobs"],
    }),
    getJobById: builder.query({
      query: (id) => ({
        url: `/jobs/${id}`,
        method: "GET",
      }),
      providesTags: ["jobs"],
    }),
    deleteJob: builder.mutation({
      query: ({ clerkId, id }) => ({
        url: `/jobs/${clerkId}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["jobs"],
    }),
    updateJob: builder.mutation({
      query: ({ clerkId, id, body }) => ({
        url: `/jobs/${clerkId}/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["jobs"],
    }),
    deleteKid: builder.mutation({
      query: ({ appId, kidId }) => ({
        url: `/application/${appId}/kid/${kidId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getData", "getSingleData"],
    }),
    deleteSpouse: builder.mutation({
      query: ({ appId, spouseId }) => ({
        url: `/application/${appId}/spouse/${spouseId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getData", "getSingleData"],
    }),
    // ───────────────────────────────────────────────
    //              BLUE COLLAR – BASIC
    // ───────────────────────────────────────────────
    blueBasic: builder.mutation({
      query: ({ body }) => ({
        url: `/basic`,
        method: "POST",
        body,
      }),
    }),
    blueBasicGet: builder.query({
      query: (id) => ({
        url: `/basic/${id}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),

    blueBasicUpdate: builder.mutation({
      query: ({ id, body }) => ({
        url: `/basic/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getData"],
    }),
    // ───────────────────────────────────────────────
    //           BLUE COLLAR – VERIFICATION
    // ───────────────────────────────────────────────
    blueVerification: builder.mutation({
      query: ({ body }) => ({
        url: `/verification/`,
        method: "POST",
        body,
      }),
    }),
    blueVerificationGet: builder.query({
      query: (id) => ({
        url: `/verification/${id}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),

    blueVerificationUpdate: builder.mutation({
      query: ({ id, body }) => ({
        url: `/verification/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getData"],
    }),
    // ───────────────────────────────────────────────
    //           BLUE COLLAR – ADDITIONAL
    // ───────────────────────────────────────────────
    blueAdditional: builder.mutation({
      query: ({ id, body }) => ({
        url: `/additional/${id}`,
        method: "POST",
        body,
      }),
    }),
    blueAdditionalGet: builder.query({
      query: (id) => ({
        url: `/additional/${id}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),

    blueAdditionalUpdate: builder.mutation({
      query: ({ id, body }) => ({
        url: `/additional/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getData"],
    }),
    // ───────────────────────────────────────────────
    //           PARTNERS PORTAL
    // ───────────────────────────────────────────────
    getPartners: builder.query({
      query: (clerkId) => ({
        url: `partners/${clerkId}`, // or whatever your route is
        method: "GET",
      }),
      providesTags: ["partners"],
    }),

    invitePartner: builder.mutation({
      query: ({ clerkId, partnerData }) => ({
        url: `/partners/invite/${clerkId}`,
        method: "POST",
        body: partnerData,
      }),
      invalidatesTags: ["partners"],
    }),

    convertToPartner: builder.mutation({
      query: ({ clerkId, partnerData }) => ({
        url: `/partners/convert/${clerkId}`,
        method: "POST",
        body: partnerData,
      }),
      invalidatesTags: ["clerkUsers", "partners"],
    }),

    updatePartnerStatus: builder.mutation({
      query: ({ clerkId, body }) => ({
        url: `/partners/${clerkId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["partners"],
    }),
    // Get All Clerk Admins
    getClerkAdmins: builder.query({
      query: (clerkId) => ({
        url: `admins/${clerkId}`,
        method: "GET",
      }),
      providesTags: ["getData", "clerkUsers"], // You can adjust tags as needed
    }),
    // Get applications submitted by a partner
    getPartnerApplications: builder.query({
      query: ({ clerkId, partnerClerkId }) => ({
        url: `partner-applications/${clerkId}/${partnerClerkId}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),
    getPartnerByClerkId: builder.query({
      query: (partnerId) => ({
        url: `partners/partner/${partnerId}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),
    reassignToNewPartner: builder.mutation({
      query: ({ applicationId, newPartnerClerkId, reason }) => ({
        url: `partners/${applicationId}/reassign-partner`,
        method: "PATCH",
        body: {
          newPartnerClerkId,
          reason: reason || "Reassigned by Admin",
        },
      }),
    }),
    getMyPartners: builder.query({
      query: (clerkId) => ({
        url: `partners/mypartners/${clerkId}`,
        method: "GET",
      }),
      providesTags: ["getData"],
    }),
  }),
});

export const {
  useGetAllQuery,
  useGetSingleQuery,
  useCheckStatusMutation,
  useUpdateUserMutation,
  useGetUserQuery,
  useBasicMutation,
  useVerificationMutation,
  useAdditionalMutation,
  useAssignTokenMutation,
  useDeleteUserMutation,
  useGetBasicQuery,
  useTokenEmailMutation,
  useGetVerificationQuery,
  useGetAdditionalQuery,
  useUpdateUserPartialMutation,
  useGetClerkUsersQuery,
  useUpdateClerkUserRoleMutation,
  useAssignApplicationMutation,
  useGetClerkAdminDataQuery,
  useCreateWorkOrderMutation,
  useCreateCompanyMutation,
  useFetchAllCompaniesQuery,
  useDeleteCompanyMutation,
  useUploadInvoiceMutation,
  useDeleteInvoiceMutation,
  useUpdateInvoiceStatusMutation,
  useDeleteUserFileMutation,
  useCreateJobMutation,
  useGetJobsQuery,
  useSearchJobsQuery,
  useGetJobByIdQuery,
  useUpdateJobStatusMutation,
  useUpdateJobMutation,
  useDeleteJobMutation,
  useDeleteKidMutation,
  useDeleteSpouseMutation,
  useBlueBasicMutation,
  useBlueVerificationMutation,
  useBlueAdditionalMutation,
  useBlueBasicGetQuery,
  useBlueVerificationGetQuery,
  useBlueAdditionalGetQuery,
  useBlueBasicUpdateMutation,
  useBlueVerificationUpdateMutation,
  useBlueAdditionalUpdateMutation,
  useGetPartnersQuery,
  useInvitePartnerMutation,
  useConvertToPartnerMutation,
  useUpdatePartnerStatusMutation,
  useGetClerkAdminsQuery,
  useGetPartnerApplicationsQuery,
  useGetPartnerByClerkIdQuery,
  useGetMyPartnersQuery,
  useReassignToNewPartnerMutation,
} = wpcApi;
